import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clipboard, Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

interface HotmartLink {
  title: string;
  url: string;
  description: string;
  category: string;
}

const HotmartPage = () => {
  const [copiedLinks, setCopiedLinks] = useState<Record<string, boolean>>({});

  const hotmartLinks: HotmartLink[] = [
    {
      title: "Link na Bio",
      url: "https://rock18430.kpages.online/link-na-bio-e9f33901-5173-41ea-9732-e65c85ad541d",
      description: "Página personalizada para compartilhar em suas redes sociais como Instagram, TikTok e outras plataformas.",
      category: "ferramentas"
    },
    {
      title: "Desenhar Cabeças - Curso De Desenho",
      url: "https://go.hotmart.com/X76584040N",
      description: "Aprenda técnicas profissionais para desenhar cabeças e rostos com precisão e realismo.",
      category: "cursos"
    },
    {
      title: "Curso de Desenho Completo",
      url: "https://go.hotmart.com/J82294036L",
      description: "Do básico ao avançado: aprenda todas as técnicas de desenho para se tornar um artista completo.",
      category: "cursos"
    },
    {
      title: "Emagreça em 7 dias",
      url: "https://go.hotmart.com/P3133182e?ap=28d5",
      description: "Programa de emagrecimento rápido com resultados visíveis em apenas uma semana.",
      category: "saude"
    },
    {
      title: "Desafio do Emagrecimento",
      url: "https://go.hotmart.com/L64628560K",
      description: "Transforme seu corpo com este desafio completo de emagrecimento e bem-estar.",
      category: "saude"
    }
  ];

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLinks({ ...copiedLinks, [url]: true });
      toast.success("Link copiado para a área de transferência!");
      
      setTimeout(() => {
        setCopiedLinks({ ...copiedLinks, [url]: false });
      }, 3000);
    });
  };

  const visitLink = (url: string) => {
    window.open(url, "_blank");
  };

  const categories = [...new Set(hotmartLinks.map(link => link.category))];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Produtos Hotmart Recomendados
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
            Uma seleção de cursos, produtos e serviços de alta qualidade disponíveis na plataforma Hotmart.
            Clique nos links ou copie para compartilhar com seus amigos.
          </p>
        </div>
        
        <Tabs defaultValue={categories[0]} className="w-full max-w-4xl mx-auto">
          <TabsList className="grid grid-cols-3 mb-8">
            {categories.map((category) => (
              <TabsTrigger 
                key={category} 
                value={category}
                className="capitalize"
              >
                {category}
              </TabsTrigger>
            ))}
          </TabsList>
          
          {categories.map((category) => (
            <TabsContent key={category} value={category} className="space-y-6">
              {hotmartLinks
                .filter(link => link.category === category)
                .map((link, index) => (
                  <Card key={index} className="overflow-hidden border-border hover:border-primary/50 transition-colors">
                    <CardHeader>
                      <CardTitle>{link.title}</CardTitle>
                      <CardDescription>{link.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-muted p-3 rounded-md flex items-center justify-between overflow-hidden">
                        <span className="text-sm text-muted-foreground truncate mr-2">{link.url}</span>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex items-center gap-1"
                            onClick={() => copyToClipboard(link.url)}
                          >
                            {copiedLinks[link.url] ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <Clipboard className="h-4 w-4" />
                            )}
                            <span className="hidden sm:inline">
                              {copiedLinks[link.url] ? "Copiado" : "Copiar"}
                            </span>
                          </Button>
                          <Button
                            size="sm"
                            className="flex items-center gap-1"
                            onClick={() => visitLink(link.url)}
                          >
                            <ArrowUpRight className="h-4 w-4" />
                            <span className="hidden sm:inline">Visitar</span>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button 
                        className="w-full" 
                        onClick={() => visitLink(link.url)}
                      >
                        Acessar {link.title}
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
            </TabsContent>
          ))}
        </Tabs>
        
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold mb-6">Sobre a Hotmart</h2>
          <p className="text-muted-foreground max-w-3xl mx-auto">
            A Hotmart é uma plataforma completa para criadores de conteúdo digital, empreendedores e afiliados 
            venderem seus produtos online. Com milhões de usuários em todo o mundo, a Hotmart oferece 
            soluções para hospedagem, pagamento, entrega e afiliação de produtos digitais.
          </p>
          <div className="mt-8">
            <Button 
              size="lg" 
              onClick={() => window.open("https://sso.hotmart.com/", "_blank")}
            >
              Criar Conta na Hotmart
            </Button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default HotmartPage;